// The one websocket connection to the bus, through the library
// (02-DESIGN/03-console/01-the-console.md § no backend): placed by the callout
// under the signed-in person's role, checked against the tenant's account,
// the carrier asked for and stamped on every request. Every read and write a
// surface makes goes through `call` and `subscribe` here; a surface never
// touches the connection. Expiry is a reconnect the person does not see: the
// credential is re-read from the session at every connect, and the session is
// refreshed before it expires. The bus mints the connection's inbox token
// itself, so that after a refusal it can read the cause the auth service kept
// under that token (decision 0016).

import { headers as natsHeaders, type Msg, type MsgHdrs, type NatsConnection } from "@nats-io/nats-core";
import {
  config,
  connect,
  connectBrowser,
  envelope,
  frame,
  identity,
  inference,
  linking,
  request,
  state,
  toolkit,
  vault,
  watch,
} from "@impire-io/sdk";

import { refresh, RefreshRefused } from "../signin/flow.ts";
import { loadSession, saveSession, type Session } from "../signin/tokens.ts";
import { CALLOUT_SERVICE } from "../wire/inference.ts";
import { discoverCallouts, type Callout } from "./callouts.ts";
import type { PlatformProfile, TenantProfile } from "./profile.ts";
import { readRefusal, type Refusal as RefusalReason } from "./refusal.ts";

/** Carrier is what the carrier names, decoded for display and never trusted for authorization. */
export type Carrier = identity.Carrier;

/** Who is the placed connection as the server and the carrier describe it. */
export interface Who {
  /** The server's user for the connection: for a callout-placed one, its attestations (hq decision 0029), never a display. */
  user: string;
  account: string;
  /** When the placement expires, in milliseconds since the epoch; absent when the server did not say. */
  expiresAt?: number;
  /** The one publish entry of the identity plane's template, when placed there. */
  plane?: { lane: string; hash: string };
  /**
   * The role the broker attested for this connection: the `role` tag the auth
   * service composed into the user at the callout, read back from `user`
   * (hq decisions 0024 point 2 and 0029). The one place the console reads its
   * role; the carrier names none. Absent when the user attests nothing.
   */
  role?: identity.Role;
  /** The carrier and what it names; absent on the identity plane, whose template admits no carrier verb. */
  carrier?: string;
  claims?: Carrier;
}

/**
 * placedTenant is the tenant a sign-in that named none was placed in
 * directly: off the identity plane, with a carrier naming the tenant.
 */
export function placedTenant(who: Who): string | undefined {
  if (who.plane !== undefined) {
    return undefined;
  }
  return who.claims?.tenant || undefined;
}

/** State is where the connection stands. */
export type State =
  | { kind: "connecting" }
  | { kind: "connected"; who: Who; reconnecting: boolean }
  | { kind: "ended"; reason: string; again: boolean }
  | { kind: "refused"; err: unknown; refusal?: RefusalReason };

/** Refusal is a request the broker did not admit or the service refused. */
export class Refusal extends Error {
  readonly code: string;

  constructor(code: string, message: string, options?: ErrorOptions) {
    super(message, options);
    this.name = "Refusal";
    this.code = code;
  }
}

/** isRefusal reports whether err is a refusal with the code. */
export function isRefusal(err: unknown, code?: string): err is Refusal {
  return err instanceof Refusal && (code === undefined || err.code === code);
}

/** CODE_NOT_ADMITTED is the code of a request the broker dropped: no answer came. */
export const CODE_NOT_ADMITTED = "not-admitted";

/** CODE_NO_SERVICE is the code of a request whose service could not be discovered: nothing serves it here. */
export const CODE_NO_SERVICE = "no-service";

/** CODE_NO_ANSWER is the code of a stream whose service sent nothing within the idle deadline. */
export const CODE_NO_ANSWER = "no-answer";

/** REFRESH_AHEAD is how long before expiry the session is refreshed. */
export const REFRESH_AHEAD = 90 * 1000;

/** DEFAULT_TIMEOUT bounds a request, in milliseconds. */
export const DEFAULT_TIMEOUT = 8000;

/** Vault is the tenant vault's three admin operations, each bearing the carrier: never a read of a value. */
export interface Vault {
  store(path: string, value: string, expectedVersion?: number): Promise<vault.StoreReply>;
  delete(path: string, all: boolean): Promise<vault.DeleteReply>;
  list(prefix: string): Promise<vault.ListReply>;
}

/**
 * Config is the configuration service's operations on one workload's
 * documents, each bearing the carrier (02-DESIGN/06-configuration/
 * 01-the-configuration-service.md): a document replaced whole under the
 * revision guard, read, removed, and the workload's documents listed. The
 * watch is a workload's own and is not spoken from a browser.
 */
export interface Config {
  set(workload: string, document: string, value: string, expected?: number): Promise<config.Entry>;
  get(workload: string, document: string): Promise<config.Entry>;
  delete(workload: string, document: string): Promise<config.Entry>;
  list(workload: string): Promise<config.Entry[]>;
}

/**
 * SolutionConfig is the configuration service's operations on a solution's
 * documents, `config.<op>.solution.<name>.<document>` — its `settings`, and
 * its `required` in a solo workspace (02-DESIGN/14-solutions/01-the-manifest.md
 * § settings and secrets).
 */
export interface SolutionConfig {
  get(solution: string, document: string): Promise<config.Entry>;
  set(solution: string, document: string, value: string, expected?: number): Promise<config.Entry>;
  delete(solution: string, document: string): Promise<config.Entry>;
  list(solution: string): Promise<config.Entry[]>;
}

/**
 * Configuration is the configuration service's five operations on one
 * workload's documents, each bearing the carrier
 * (02-DESIGN/06-configuration/01-the-configuration-service.md § subjects): a
 * set guarded by the revision it was read at is refused `conflict` when
 * anything landed since; the watch is the leased watch, reopened by the
 * library so the page never sees the seam.
 */
export interface Configuration {
  list(workload: string): Promise<config.Entry[]>;
  get(workload: string, document: string): Promise<config.Entry>;
  set(workload: string, document: string, value: string, expected?: number): Promise<config.Entry>;
  delete(workload: string, document: string): Promise<config.Entry>;
  watch(workload: string, signal: AbortSignal): AsyncGenerator<watch.Event<config.Entry>, void, undefined>;
  /** watchDocument follows one document, `config.watch.workload.<id>.<document>`. */
  watchDocument(
    workload: string,
    document: string,
    signal: AbortSignal,
  ): AsyncGenerator<watch.Event<config.Entry>, void, undefined>;
}

/**
 * Stores is the state service's verbs as the Stores surface speaks them, each
 * bearing the carrier (02-DESIGN/10-state/02-the-state-service.md § subjects):
 * the admin's store, type and index verbs, and the reads of an instance's
 * state and history. A tenant without a state service answers `no-service`.
 */
export interface Stores {
  listStores(): Promise<state.Store[]>;
  getStore(name: string): Promise<state.Store>;
  createStore(name: string, req: state.StoreRequest): Promise<state.Store>;
  updateStore(name: string, req: state.StoreUpdate): Promise<state.Store>;
  deleteStore(name: string): Promise<void>;
  listTypes(store: string): Promise<state.NamedType[]>;
  getType(store: string, typ: string): Promise<state.TypeRecord>;
  defineType(store: string, typ: string, rec: state.TypeRecord, expected?: number): Promise<state.TypeRecord>;
  listIndexes(store: string): Promise<state.NamedIndex[]>;
  declareIndex(store: string, index: string, decl: state.IndexDeclaration): Promise<state.IndexDeclaration>;
  deleteIndex(store: string, index: string): Promise<void>;
  /** The instances of a type with their current state, collected. */
  list(store: string, typ: string): Promise<state.Instance[]>;
  /** One page of every item of the store, typed or not, in path order, each with its type and last change; never its state. */
  listItems(store: string, page?: { after?: string; limit?: number }): Promise<state.ItemPage>;
  get(store: string, path: string): Promise<state.Current>;
  /**
   * One page of the instance's history, newest first, before a change when
   * one is named (decision 0062): its changes, every change counted, and the
   * change to read before for the next page, absent on the oldest.
   */
  history(store: string, path: string, page?: { before?: number; limit?: number }): Promise<state.Changes>;
  /** A search index's hits, best first, and the count of everything that matched; refused index-not-ready while it rebuilds. */
  search(store: string, index: string, q: state.SearchQuery): Promise<state.SearchResult>;
}

/** Record is one message a subscription delivered. */
export interface Record_ {
  subject: string;
  data: Uint8Array;
  header(name: string): string | undefined;
  json(): unknown;
}

const decoder = new TextDecoder();

/** Dial places a connection over the profile's websocket URL: the library's connectBrowser, the one seam a connection is opened through. */
export type Dial = typeof connectBrowser;

/** Bus is the console's one connection. */
export class Bus {
  private nc: NatsConnection | undefined;
  /** The connection's carriers, one per audience (hq decision 0024); absent on the identity plane. */
  private carriers: identity.Carriers | undefined;
  /** The one placement under way or made: a second connect answers it instead of dialling again. */
  private placing: Promise<Who> | undefined;
  private readonly caller = new envelope.Caller();
  private state: State = { kind: "connecting" };
  private readonly listeners = new Set<(s: State) => void>();
  private refreshTimer: ReturnType<typeof setTimeout> | undefined;
  private closed = false;
  private readonly violations = new Map<string, number>();
  /** The inbox token this connection presents, minted once and kept across every reconnect. */
  readonly inbox = connect.newInbox();

  constructor(
    readonly origin: string,
    readonly platform: PlatformProfile,
    private target: TenantProfile | null,
    readonly lane: "oidc" | "github",
    private readonly dial: Dial = connectBrowser,
    /**
     * plane asks for the identity plane, `-` as the tenant (hq decision 0072):
     * the sign-in is placed there whatever its memberships. Only with no
     * workspace named.
     */
    readonly plane = false,
  ) {}

  /** tenant is the workspace this connection serves, or null on the identity plane. */
  get tenant(): TenantProfile | null {
    return this.target;
  }

  /**
   * adopt makes a connection the sign-in placed directly in a workspace — no
   * tenant named, exactly one membership (03-signing-in.md § sign-up) — that
   * workspace's connection, so entering it opens no second one. It refuses,
   * and changes nothing, unless the connection is placed and its account and
   * carrier are the workspace's.
   */
  adopt(tenant: TenantProfile): boolean {
    const who = this.who();
    if (
      this.closed ||
      who === undefined ||
      placedTenant(who) !== tenant.slug ||
      who.account !== tenant.account
    ) {
      return false;
    }
    this.target = tenant;
    return true;
  }

  /** current is where the connection stands now. */
  current(): State {
    return this.state;
  }

  /** onChange is told at every change of state. */
  onChange(l: (s: State) => void): () => void {
    this.listeners.add(l);
    return () => {
      this.listeners.delete(l);
    };
  }

  private set(s: State): void {
    this.state = s;
    for (const l of this.listeners) {
      l(s);
    }
  }

  /** who is the placed connection, or undefined before placement. */
  who(): Who | undefined {
    return this.state.kind === "connected" ? this.state.who : undefined;
  }

  /**
   * connect places the connection and asks for the carrier. It resolves once
   * placed, or throws the refusal. It dials once: a second call answers the
   * first one's placement, and the library's own reconnect is the only other
   * connect this connection ever makes.
   */
  connect(): Promise<Who> {
    this.placing ??= this.place0();
    return this.placing;
  }

  private async place0(): Promise<Who> {
    const session = loadSession();
    if (session === undefined) {
      const err = new Refusal("signed-out", "no session: sign in");
      this.set({ kind: "refused", err });
      throw err;
    }
    try {
      const nc = await this.dial(this.platform, {
        lane: this.lane,
        credential: () => loadSession()?.credential ?? "",
        tenant: this.target === null ? undefined : { slug: this.target.slug, account: this.target.account },
        plane: this.target === null && this.plane,
        inbox: this.inbox,
        name: "console",
      });
      this.nc = nc;
      const who = await this.place(nc, session);
      this.set({ kind: "connected", who, reconnecting: false });
      void this.watch(nc);
      return who;
    } catch (err) {
      await this.nc?.close().catch(() => undefined);
      this.nc = undefined;
      this.carriers = undefined;
      // The server says Authorization Violation and nothing more; the cause the
      // auth service kept under this connection's inbox token is read once.
      const refusal = await readRefusal(this.origin, this.inbox).catch(() => undefined);
      this.set({ kind: "refused", err, refusal });
      throw err;
    }
  }

  private async place(nc: NatsConnection, session: Session): Promise<Who> {
    const info = await connect.whoAmI(nc);
    const who: Who = { user: info.user, account: info.account };
    const attested = identity.parseAttestations(info.user);
    if (attested !== undefined) {
      who.role = attested.role;
    }
    if (info.expires !== undefined && info.expires > 0) {
      who.expiresAt = Date.now() + info.expires / 1e6;
    }
    const plane = identity.planeOf(info.permissions?.publish?.allow ?? []);
    if (plane !== undefined) {
      who.plane = plane;
    }
    // A tenant page reads its carrier; so does a sign-in that named no tenant
    // and was not put on the plane — a person with exactly one tenant is
    // placed there directly, and the carrier is what names it.
    // Every carrier is addressed to the one service it is sent to and asked
    // from the placed connection, with no credential (hq decision 0024); the
    // one addressed to the identity verbs is what the shell reads.
    this.carriers = undefined;
    if (this.target !== null || plane === undefined) {
      this.carriers = new identity.Carriers(nc, this.caller);
      who.carrier = await this.carriers.forAudience(identity.audienceOf(identity.carrierSubject()));
      who.claims = identity.decodeCarrier(who.carrier);
    }
    this.scheduleRefresh(session, who.expiresAt);
    return who;
  }

  private scheduleRefresh(session: Session, placementExpiresAt: number | undefined): void {
    if (this.refreshTimer !== undefined) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = undefined;
    }
    const candidates = [session.expiresAt, placementExpiresAt].filter((v): v is number => v !== undefined);
    if (candidates.length === 0 || session.refreshToken === undefined) {
      return;
    }
    const at = Math.min(...candidates) - REFRESH_AHEAD;
    const delay = Math.max(1000, at - Date.now());
    this.refreshTimer = setTimeout(() => {
      void this.refreshNow();
    }, delay);
  }

  /** refreshNow refreshes the session's credential; a refusal ends the sign-in. */
  async refreshNow(): Promise<void> {
    const session = loadSession();
    if (session === undefined || this.closed) {
      return;
    }
    try {
      const next = await refresh(session);
      saveSession(next);
      const who = this.who();
      this.scheduleRefresh(next, who?.expiresAt);
    } catch (err) {
      if (err instanceof RefreshRefused) {
        await this.end("the sign-in could not be renewed: sign in again", true);
        return;
      }
      // A network failure: try again shortly, before the server closes the connection.
      this.refreshTimer = setTimeout(() => {
        void this.refreshNow();
      }, 15 * 1000);
    }
  }

  private async watch(nc: NatsConnection): Promise<void> {
    try {
      for await (const s of nc.status()) {
        if (this.closed) {
          return;
        }
        switch (s.type) {
          case "disconnect":
          case "reconnecting": {
            const st = this.state;
            if (st.kind === "connected" && !st.reconnecting) {
              this.set({ ...st, reconnecting: true });
            }
            break;
          }
          case "reconnect": {
            const session = loadSession();
            if (session === undefined) {
              await this.end("signed out", true);
              return;
            }
            try {
              const who = await this.place(nc, session);
              // A reconnect is placed afresh: it must land in the workspace's account again.
              if (this.target !== null && who.account !== this.target.account) {
                await this.end("the sign-in no longer reaches this workspace: sign in again", true);
                return;
              }
              this.set({ kind: "connected", who, reconnecting: false });
            } catch (err) {
              this.set({ kind: "refused", err });
            }
            break;
          }
          case "error": {
            const text = s.error.message;
            const m = /Permissions Violation for (?:Publish|Subscription) to "([^"]+)"/.exec(text);
            if (m?.[1] !== undefined) {
              this.violations.set(m[1], Date.now());
            } else if (/Authorization Violation|authentication failed/i.test(text)) {
              await this.end("the sign-in was refused at reconnect: sign in again", true);
              return;
            }
            break;
          }
          case "close": {
            if (this.state.kind === "connected") {
              this.set({
                kind: "ended",
                reason: "the session ended: the connection could not be kept",
                again: true,
              });
            }
            return;
          }
          default:
            break;
        }
      }
    } catch {
      // the status iterator ends with the connection
    }
  }

  private async end(reason: string, again: boolean): Promise<void> {
    if (this.refreshTimer !== undefined) {
      clearTimeout(this.refreshTimer);
    }
    const nc = this.nc;
    this.nc = undefined;
    this.carriers = undefined;
    this.set({ kind: "ended", reason, again });
    await nc?.close().catch(() => undefined);
  }

  /** close ends the connection: signing out, or leaving the tenant. */
  async close(): Promise<void> {
    this.closed = true;
    await this.end("closed", false);
  }

  /** The carrier keys attestations are verified against, read once per connection and again on the hour. */
  private keys: { nc: NatsConnection; set: identity.KeySet; at: number } | undefined;

  /**
   * trust is what a discovery keeps an answer by (decision 0026): the carrier
   * keys, read from the auth service, and the tenant this connection serves. A
   * model or a toolkit whose attestation does not vouch it is not shown.
   */
  private async trust(
    nc: NatsConnection,
    who: Who,
    timeout: number,
  ): Promise<{ keys: identity.KeySet; tenant: string }> {
    const tenant = this.target?.slug ?? placedTenant(who) ?? "";
    if (this.keys === undefined || this.keys.nc !== nc || Date.now() - this.keys.at > 3_600_000) {
      const set = identity.keySet(await identity.fetchKeys(nc, this.caller, { timeout }));
      this.keys = { nc, set, at: Date.now() };
    }
    return { keys: this.keys.set, tenant };
  }

  private connection(): { nc: NatsConnection; who: Who } {
    const nc = this.nc;
    const who = this.who();
    if (nc === undefined || who === undefined) {
      throw new Refusal("not-connected", "not connected");
    }
    return { nc, who };
  }

  /**
   * call sends a sealed request bearing the carrier and returns the opened
   * reply. A service's refusal is a Refusal with its code; a request the
   * broker dropped comes back as `not-admitted`, never as a hang.
   */
  async call<Rep>(service: string, subj: string, body?: unknown, timeout = DEFAULT_TIMEOUT): Promise<Rep> {
    const { nc } = this.connection();
    try {
      return await request<Rep>(nc, this.caller, service, subj, body, {
        headers: await this.carrierHeaders(subj),
        timeout,
      });
    } catch (err) {
      throw this.refusalOf(err, service, subj, timeout);
    }
  }

  /**
   * callText is call with a body that is text sent as it is rather than as
   * JSON — a blueprint, which the service stores as the document it is — and
   * headers of the family's own beside the carrier: the revision guard.
   */
  async callText<Rep>(
    service: string,
    subj: string,
    text: string,
    extra?: Record<string, string>,
    timeout = DEFAULT_TIMEOUT,
  ): Promise<Rep> {
    const { nc } = this.connection();
    try {
      let hdrs = await this.carrierHeaders(subj);
      if (extra !== undefined) {
        hdrs ??= natsHeaders();
        for (const [k, v] of Object.entries(extra)) {
          hdrs.set(k, v);
        }
      }
      const reply = await this.caller.request(nc, service, subj, new TextEncoder().encode(text), {
        headers: hdrs,
        timeout,
      });
      return JSON.parse(new TextDecoder().decode(reply.data)) as Rep;
    } catch (err) {
      throw this.refusalOf(err, service, subj, timeout);
    }
  }

  /**
   * carrierKeys is the carrier keys this connection verifies attestations
   * against: what a page verifies a steward's report with itself, so that
   * *attested* is the page's verification and not the service's word.
   */
  async carrierKeys(timeout = DEFAULT_TIMEOUT): Promise<identity.KeySet> {
    const { nc, who } = this.connection();
    return (await this.trust(nc, who, timeout)).keys;
  }

  /**
   * vault is the tenant vault's admin operations through the library's
   * client, every request bearing the carrier and every refusal reported as
   * one; a tenant without a vault answers `no-service`.
   */
  vault(timeout = DEFAULT_TIMEOUT): Vault {
    const { nc } = this.connection();
    const client = new vault.Client(nc, this.caller, { timeout });
    const guard = async <T>(
      op: vault.Op,
      path: string,
      run: (opts: { headers?: MsgHdrs; timeout: number }) => Promise<T>,
    ): Promise<T> => {
      // The library's builder puts the root in front, so the carrier is
      // addressed to the vault; a subject without it has no audience.
      const subj = vault.subject(op, path);
      try {
        return await run({ headers: await this.carrierHeaders(subj), timeout });
      } catch (err) {
        throw this.refusalOf(err, vault.SERVICE, subj, timeout);
      }
    };
    return {
      store: (path, value, expectedVersion) =>
        guard(vault.Op.store, path, (opts) => client.store(path, value, expectedVersion, opts)),
      delete: (path, all) => guard(vault.Op.delete, path, (opts) => client.delete(path, all, opts)),
      list: (prefix) => guard(vault.Op.list, prefix, (opts) => client.list(prefix, opts)),
    };
  }

  /**
   * config is the configuration service's set, get, delete and list as the
   * toolkit pages speak them: the same client as configuration, without the watch.
   */
  config(timeout = DEFAULT_TIMEOUT): Config {
    return this.configuration(timeout);
  }

  /**
   * carrierHeaders are the headers a request on subj bears beside the sealing
   * header: the carrier addressed to the subject's audience, none on the
   * identity plane.
   */
  private async carrierHeaders(subj: string): Promise<MsgHdrs | undefined> {
    if (this.carriers === undefined) {
      return undefined;
    }
    return identity.withCarrier(await this.carriers.forSubject(subj));
  }

  /**
   * configuration is the configuration service's operations through the
   * library's client, every request bearing the carrier and every refusal
   * reported as one.
   */
  configuration(timeout = DEFAULT_TIMEOUT): Configuration {
    const { nc } = this.connection();
    const client = new config.Client(nc, this.caller, { timeout, carrier: this.carriers?.carrier });
    const opts = { timeout };
    const guard = async <T>(subj: string, run: () => Promise<T>): Promise<T> => {
      try {
        return await run();
      } catch (err) {
        throw this.refusalOf(err, config.SERVICE, subj, timeout);
      }
    };
    const subj = (op: config.Op, w: string, d = "") => config.subject(op, config.Kind.workload, w, d);
    const refusalOf = (err: unknown, s: string) => this.refusalOf(err, config.SERVICE, s, timeout);
    return {
      list: (w) => guard(subj(config.Op.list, w), () => client.list(w, opts)),
      get: (w, d) => guard(subj(config.Op.get, w, d), () => client.get(w, d, opts)),
      set: (w, d, value, expected) =>
        guard(subj(config.Op.set, w, d), () => client.set(w, d, value, expected, opts)),
      delete: (w, d) => guard(subj(config.Op.delete, w, d), () => client.delete(w, d, opts)),
      watch: async function* (w, signal) {
        try {
          yield* client.watch(w, "", { signal });
        } catch (err) {
          if (signal.aborted) {
            return;
          }
          throw refusalOf(err, subj(config.Op.watch, w));
        }
      },
      watchDocument: async function* (w, d, signal) {
        try {
          yield* client.watch(w, d, { signal });
        } catch (err) {
          if (signal.aborted) {
            return;
          }
          throw refusalOf(err, subj(config.Op.watch, w, d));
        }
      },
    };
  }

  /**
   * solutionConfig is the configuration service's operations on solutions'
   * documents through the library's client, every request bearing the
   * carrier and every refusal reported as one.
   */
  solutionConfig(timeout = DEFAULT_TIMEOUT): SolutionConfig {
    const { nc } = this.connection();
    const client = new config.Client(nc, this.caller, {
      timeout,
      carrier: this.carriers?.carrier,
      kind: config.Kind.solution,
    });
    const opts = { timeout };
    const guard = async <T>(subj: string, run: () => Promise<T>): Promise<T> => {
      try {
        return await run();
      } catch (err) {
        throw this.refusalOf(err, config.SERVICE, subj, timeout);
      }
    };
    const subj = (op: config.Op, n: string, d = "") => config.subject(op, config.Kind.solution, n, d);
    return {
      list: (n) => guard(subj(config.Op.list, n), () => client.list(n, opts)),
      get: (n, d) => guard(subj(config.Op.get, n, d), () => client.get(n, d, opts)),
      set: (n, d, value, expected) =>
        guard(subj(config.Op.set, n, d), () => client.set(n, d, value, expected, opts)),
      delete: (n, d) => guard(subj(config.Op.delete, n, d), () => client.delete(n, d, opts)),
    };
  }

  /**
   * discoverToolkits scatters the toolkit service's discovery on this
   * connection and answers every toolkit it can see — what the workspace
   * offers this person (02-DESIGN/08-toolkits/01-the-toolkit-contract.md §
   * discovering and calling from a generic client). Nothing is cached.
   */
  async discoverToolkits(timeout = DEFAULT_TIMEOUT): Promise<toolkit.Toolkit[]> {
    const { nc, who } = this.connection();
    try {
      return await toolkit.discover(nc, { timeout, ...(await this.trust(nc, who, timeout)) });
    } catch (err) {
      throw this.refusalOf(err, toolkit.SERVICE, toolkit.SERVICE, timeout);
    }
  }

  /**
   * stores is the state service's verbs through the library's client, which
   * stamps the carrier itself because the author of every change is read
   * from it; every refusal is reported as one, and a tenant whose state
   * service does not run yet answers `no-service`.
   */
  stores(timeout = DEFAULT_TIMEOUT): Stores {
    const { nc } = this.connection();
    // The library's client is given the carrier addressed to the state service.
    const stateClient = async () =>
      new state.Client(
        nc,
        this.caller,
        this.carriers === undefined
          ? ""
          : await this.carriers.forAudience(identity.audienceOf(state.storeListSubject())),
        { timeout },
      );
    const guard = async <T>(subj: string, run: (client: state.Client) => Promise<T>): Promise<T> => {
      try {
        return await run(await stateClient());
      } catch (err) {
        throw this.refusalOf(err, state.SERVICE, subj, timeout);
      }
    };
    const collect = async <T>(
      subj: string,
      it: (client: state.Client) => AsyncGenerator<T, void, undefined>,
    ): Promise<T[]> => {
      const out: T[] = [];
      try {
        for await (const v of it(await stateClient())) {
          out.push(v);
        }
      } catch (err) {
        throw this.refusalOf(err, state.SERVICE, subj, timeout);
      }
      return out;
    };
    return {
      listStores: () => guard(state.storeListSubject(), (client) => client.listStores()),
      getStore: (name) => guard(state.storeSubject(state.Act.get, name), (client) => client.getStore(name)),
      createStore: (name, req) =>
        guard(state.storeSubject(state.Act.create, name), (client) => client.createStore(name, req)),
      updateStore: (name, req) =>
        guard(state.storeSubject(state.Act.update, name), (client) => client.updateStore(name, req)),
      deleteStore: (name) =>
        guard(state.storeSubject(state.Act.delete, name), (client) => client.deleteStore(name)),
      listTypes: (store) => guard(state.typeListSubject(store), (client) => client.listTypes(store)),
      getType: (store, typ) =>
        guard(state.typeSubject(state.Act.get, store, typ), (client) => client.getType(store, typ)),
      defineType: (store, typ, rec, expected) =>
        guard(state.typeSubject(state.Act.define, store, typ), (client) =>
          client.defineType(store, typ, rec, expected),
        ),
      listIndexes: (store) => guard(state.indexListSubject(store), (client) => client.listIndexes(store)),
      declareIndex: (store, index, decl) =>
        guard(state.indexSubject(state.Act.declare, store, index), (client) =>
          client.declareIndex(store, index, decl),
        ),
      deleteIndex: (store, index) =>
        guard(state.indexSubject(state.Act.delete, store, index), (client) =>
          client.deleteIndex(store, index),
        ),
      list: (store, typ) => collect(state.listSubject(store, typ), (client) => client.list(store, typ)),
      listItems: (store, page) =>
        guard(state.storeItemsSubject(store), (client) => client.listItems(store, page)),
      get: (store, path) =>
        guard(state.instanceSubject(state.Verb.get, store, path), (client) => client.get(store, path)),
      history: (store, path, page) =>
        guard(state.instanceSubject(state.Verb.history, store, path), (client) =>
          client.historyPage(store, path, page),
        ),
      search: (store, index, q) =>
        guard(state.indexSubject(state.Act.query, store, index), (client) => client.search(store, index, q)),
    };
  }

  /**
   * models scatters the inference discovery on this connection and answers
   * every ready instance: discovery is the catalogue, and nothing is cached
   * (02-DESIGN/05-inference/01-models-and-instances.md § discovery is the catalogue).
   */
  async models(): Promise<inference.Instance[]> {
    const { nc, who } = this.connection();
    try {
      return await inference.discover(nc, await this.trust(nc, who, DEFAULT_TIMEOUT));
    } catch (err) {
      throw this.refusalOf(err, inference.SERVICE, inference.SERVICE, DEFAULT_TIMEOUT);
    }
  }

  /**
   * callouts scatters the callout service's discovery on this connection and
   * answers every callout the platform vouches for: what the Models surface
   * says beside a model whose required document names one
   * (02-DESIGN/05-inference/01-models-and-instances.md § the console: Models).
   * Nothing is cached.
   */
  async callouts(): Promise<Callout[]> {
    const { nc, who } = this.connection();
    try {
      return await discoverCallouts(nc, await this.trust(nc, who, DEFAULT_TIMEOUT));
    } catch (err) {
      throw this.refusalOf(err, CALLOUT_SERVICE, CALLOUT_SERVICE, DEFAULT_TIMEOUT);
    }
  }

  /**
   * exchangeConfidential exchanges an authorization code through a
   * confidential client's toolkit — its reserved exchange verb, the request
   * sealed toward the key it advertised and bearing the carrier the verb
   * requires — and answers the refresh token the console deposits
   * (02-DESIGN/08-toolkits/03-linking.md § the flow). The toolkit deposits
   * nothing.
   */
  async exchangeConfidential(tk: toolkit.Toolkit, x: linking.Exchange, timeout = 30000): Promise<string> {
    const { nc } = this.connection();
    try {
      return await linking.exchangeConfidential(nc, this.caller, tk, x, {
        headers: await this.carrierHeaders(toolkit.exchangeSubject(tk.workload)),
        idle: timeout,
      });
    } catch (err) {
      throw this.refusalOf(err, toolkit.SERVICE, toolkit.exchangeSubject(tk.workload), timeout);
    }
  }

  /** refusalOf turns what a request threw into the refusal a surface shows. */
  private refusalOf(err: unknown, service: string, subj: string, timeout: number): Refusal {
    if (err instanceof Refusal) {
      return err;
    }
    if (err instanceof envelope.ServiceError) {
      return new Refusal(err.code, err.message === "" ? err.code : err.message, { cause: err });
    }
    const name = err instanceof Error ? err.name : "";
    const text = err instanceof Error ? err.message : String(err);
    if (/envelope: discover /.test(text)) {
      return new Refusal(CODE_NO_SERVICE, `nothing serves ${service} here: ${text}`, { cause: err });
    }
    if (name === "IdleError" || /stream idle/.test(text)) {
      return new Refusal(CODE_NO_ANSWER, "no answer in time", { cause: err });
    }
    if (name === "NoRespondersError" || /NoResponders|no responders/i.test(text)) {
      return new Refusal(CODE_NO_SERVICE, `nothing serves ${service} here`, { cause: err });
    }
    const violated = this.violations.get(subj);
    if (violated !== undefined && Date.now() - violated < timeout + 1000) {
      this.violations.delete(subj);
      return new Refusal(CODE_NOT_ADMITTED, "not admitted for this role: the broker dropped the request", {
        cause: err,
      });
    }
    if (/timeout|TimeoutError|NoResponders/i.test(name + " " + text)) {
      return new Refusal(
        CODE_NOT_ADMITTED,
        /NoResponders/i.test(name + " " + text)
          ? "nothing serves this verb here"
          : "no answer: the request was not admitted, or the service did not answer in time",
        { cause: err },
      );
    }
    return new Refusal("failed", text, { cause: err });
  }

  /**
   * follow opens a leased watch on a subject the template admits, with the
   * family's three watch headers (02-DESIGN/06-configuration/01-the-configuration-service.md
   * § the leased watch): the snapshot, its completion, then each change; the
   * library reopens it from the last revision at every lease, so the page
   * never sees the seam. It ends quietly when the signal aborts.
   */
  async *follow<T>(
    service: string,
    subj: string,
    headers: watch.Headers,
    signal: AbortSignal,
  ): AsyncGenerator<watch.Event<T>, void, undefined> {
    const { nc } = this.connection();
    try {
      yield* watch.open<T>(
        nc,
        this.caller,
        { service, subject: subj, watchHeaders: headers, carrier: this.carriers?.carrier, signal },
        (d) => JSON.parse(decoder.decode(d)) as T,
      );
    } catch (err) {
      if (signal.aborted) {
        return;
      }
      throw this.refusalOf(err, service, subj, DEFAULT_TIMEOUT);
    }
  }

  /**
   * stream sends a sealed request bearing the carrier and hands each content
   * frame of the reply, opened and parsed, to onFrame — one answer for a
   * one-shot reply, every frame to the terminator for a stream — and, while
   * `follow` holds and the signal has not aborted, asks again when the
   * reply ends, which is how a leased watch is kept open
   * (02-DESIGN/14-solutions/03-the-console.md § the elements: a list read to
   * the terminator, or live where the verb is a watch).
   */
  async stream(
    service: string,
    subj: string,
    body: unknown,
    onFrame: (v: unknown) => void,
    { signal, follow = false, idle }: { signal?: AbortSignal; follow?: boolean; idle?: number } = {},
  ): Promise<void> {
    const data = body === undefined ? new Uint8Array(0) : new TextEncoder().encode(JSON.stringify(body));
    for (;;) {
      const { nc } = this.connection();
      try {
        const r = await frame.open(nc, this.caller, {
          service,
          subject: subj,
          body: data,
          headers: await this.carrierHeaders(subj),
          signal,
          idle,
        });
        for await (const f of r.frames()) {
          if (f.kind === frame.Kind.content && f.data.length > 0) {
            onFrame(JSON.parse(decoder.decode(f.data)) as unknown);
          }
        }
      } catch (err) {
        if (signal?.aborted === true) {
          return;
        }
        throw this.refusalOf(err, service, subj, idle ?? DEFAULT_TIMEOUT);
      }
      if (!follow || signal?.aborted === true) {
        return;
      }
    }
  }

  /**
   * open places a streaming request and hands back its reader, for an
   * element that reads the frames themselves — a chat's text and progress
   * frames and its terminator's headers — rather than JSON rows. A refusal
   * on opening is thrown in the console's words; what the frames throw after
   * is the reader's.
   */
  async open(
    service: string,
    subj: string,
    body: unknown,
    { signal, idle }: { signal?: AbortSignal; idle?: number } = {},
  ): Promise<frame.Reader> {
    const data = body === undefined ? new Uint8Array(0) : new TextEncoder().encode(JSON.stringify(body));
    const { nc } = this.connection();
    try {
      return await frame.open(nc, this.caller, {
        service,
        subject: subj,
        body: data,
        headers: await this.carrierHeaders(subj),
        signal,
        idle,
      });
    } catch (err) {
      throw this.refusalOf(err, service, subj, idle ?? DEFAULT_TIMEOUT);
    }
  }

  /**
   * subscribe follows a subject the template admits. The handler gets each
   * record; `onEnd` gets the refusal when the broker closes the subscription.
   * It returns the unsubscribe.
   */
  subscribe(subj: string, handler: (r: Record_) => void, onEnd?: (err: unknown) => void): () => void {
    const { nc } = this.connection();
    const sub = nc.subscribe(subj);
    void (async () => {
      try {
        for await (const m of sub) {
          handler(wrap(m));
        }
      } catch (err) {
        onEnd?.(err);
      }
    })();
    void sub.closed.then((err) => {
      if (err instanceof Error) {
        onEnd?.(new Refusal(CODE_NOT_ADMITTED, `not admitted for this role: ${err.message}`, { cause: err }));
      }
    });
    return () => {
      sub.unsubscribe();
    };
  }
}

function wrap(m: Msg): Record_ {
  return {
    subject: m.subject,
    data: m.data,
    header: (name) => m.headers?.get(name),
    json: () => JSON.parse(decoder.decode(m.data)) as unknown,
  };
}
